import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Inbox, Star, Clock, Send, FileText,
  Bookmark, CalendarClock, Mail, AlertTriangle, Trash2,
  Users2, BellRing, MessagesSquare, Tag,
  Plus, Settings, RefreshCw, Menu, X,
  ChevronDown, ChevronRight,
  Settings2, MailPlus,
  Rss, BellOff, ShieldOff, ExternalLink,
  Pencil, Trash, Check, Circle,
} from 'lucide-react'
import { useMailStore } from '../../stores/mailStore'
import { useUIStore } from '../../stores/uiStore'
import type { Label } from '../../lib/types'

// ─── Icon map for system folders ─────────────────────────────────────────────

const SYSTEM_ICONS: Record<string, React.ElementType> = {
  inbox:      Inbox,
  starred:    Star,
  snoozed:    Clock,
  sent:       Send,
  drafts:     FileText,
  important:  Bookmark,
  scheduled:  CalendarClock,
  all_mail:   Mail,
  spam:       AlertTriangle,
  trash:      Trash2,
  social:     Users2,
  updates:    BellRing,
  forums:     MessagesSquare,
  promotions: Tag,
}

// ─── Label colour palette ─────────────────────────────────────────────────────

const LABEL_COLORS = [
  '#4F8EF7', '#6DB87A', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#84CC16', '#06B6D4',
]

// ─── Mock subscription data ───────────────────────────────────────────────────

interface Subscription {
  id: string
  sender: string
  email: string
  lastEmail: string
  count: number
  status: 'active' | 'unsubscribed' | 'blocked'
}

const MOCK_SUBSCRIPTIONS: Subscription[] = [
  { id: 's1', sender: 'The Verge',         email: 'newsletter@theverge.com',        lastEmail: '2 hours ago',   count: 48,  status: 'active' },
  { id: 's2', sender: 'Hacker Newsletter', email: 'hi@hackernewsletter.com',         lastEmail: 'Yesterday',     count: 124, status: 'active' },
  { id: 's3', sender: 'Morning Brew',      email: 'crew@morningbrew.com',            lastEmail: '3 days ago',    count: 89,  status: 'active' },
  { id: 's4', sender: 'Product Hunt',      email: 'noreply@producthunt.com',         lastEmail: '1 week ago',    count: 31,  status: 'unsubscribed' },
  { id: 's5', sender: 'GitHub',            email: 'noreply@github.com',              lastEmail: '1 hour ago',    count: 203, status: 'active' },
  { id: 's6', sender: 'Stripe Updates',    email: 'updates@stripe.com',              lastEmail: '2 weeks ago',   count: 12,  status: 'active' },
  { id: 's7', sender: 'Mailchimp',         email: 'mc-marketing@mailchimp.com',      lastEmail: '1 month ago',   count: 5,   status: 'blocked' },
]

// ─── Manage Subscriptions Modal ───────────────────────────────────────────────

function ManageSubscriptionsModal({ onClose }: { onClose: () => void }) {
  const [subs, setSubs] = useState<Subscription[]>(MOCK_SUBSCRIPTIONS)
  const [filter, setFilter] = useState<'all' | 'active' | 'unsubscribed' | 'blocked'>('all')
  const [search, setSearch] = useState('')

  const filtered = subs.filter((s) => {
    if (filter !== 'all' && s.status !== filter) return false
    if (search && !s.sender.toLowerCase().includes(search.toLowerCase()) &&
        !s.email.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const setStatus = (id: string, status: Subscription['status']) =>
    setSubs((prev) => prev.map((s) => s.id === id ? { ...s, status } : s))

  const activeCount = subs.filter((s) => s.status === 'active').length

  return createPortal(
    <>
      <motion.div
        className="sb-modal-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="sb-modal sb-subs-modal"
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        role="dialog"
        aria-modal="true"
        aria-label="Manage subscriptions"
      >
        {/* Header */}
        <div className="sb-modal__header">
          <div className="sb-modal__title-row">
            <Rss size={16} style={{ color: 'var(--brand)' }} />
            <h2>Manage Subscriptions</h2>
            <span className="sb-badge">{activeCount} active</span>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={15} /></button>
        </div>

        {/* Search + filter bar */}
        <div className="sb-subs__toolbar">
          <input
            className="sb-search"
            placeholder="Search senders…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="sb-filter-tabs">
            {(['all', 'active', 'unsubscribed', 'blocked'] as const).map((f) => (
              <button
                key={f}
                className={`sb-filter-tab${filter === f ? ' sb-filter-tab--active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="sb-subs__list">
          {filtered.length === 0 && (
            <div className="sb-empty">No subscriptions found</div>
          )}
          {filtered.map((sub) => (
            <div key={sub.id} className={`sb-sub-row${sub.status !== 'active' ? ' sb-sub-row--muted' : ''}`}>
              <div className="sb-sub-row__avatar">
                {sub.sender.charAt(0).toUpperCase()}
              </div>
              <div className="sb-sub-row__info">
                <div className="sb-sub-row__name">{sub.sender}</div>
                <div className="sb-sub-row__email">{sub.email}</div>
              </div>
              <div className="sb-sub-row__meta">
                <span className="sb-sub-row__last">{sub.lastEmail}</span>
                <span className="sb-sub-row__count">{sub.count} emails</span>
              </div>
              <div className="sb-sub-row__status">
                {sub.status === 'active' && <span className="sb-pill sb-pill--green">Active</span>}
                {sub.status === 'unsubscribed' && <span className="sb-pill sb-pill--gray">Unsub'd</span>}
                {sub.status === 'blocked' && <span className="sb-pill sb-pill--red">Blocked</span>}
              </div>
              <div className="sb-sub-row__actions">
                {sub.status === 'active' && (
                  <>
                    <button
                      className="sb-action-btn"
                      title="Unsubscribe"
                      onClick={() => setStatus(sub.id, 'unsubscribed')}
                    >
                      <BellOff size={13} />
                      <span>Unsubscribe</span>
                    </button>
                    <button
                      className="sb-action-btn sb-action-btn--danger"
                      title="Block sender"
                      onClick={() => setStatus(sub.id, 'blocked')}
                    >
                      <ShieldOff size={13} />
                    </button>
                  </>
                )}
                {sub.status === 'unsubscribed' && (
                  <button
                    className="sb-action-btn"
                    title="Re-subscribe"
                    onClick={() => setStatus(sub.id, 'active')}
                  >
                    <Rss size={13} />
                    <span>Re-subscribe</span>
                  </button>
                )}
                {sub.status === 'blocked' && (
                  <button
                    className="sb-action-btn"
                    title="Unblock"
                    onClick={() => setStatus(sub.id, 'active')}
                  >
                    <Check size={13} />
                    <span>Unblock</span>
                  </button>
                )}
                <a
                  className="sb-action-btn"
                  href={`mailto:${sub.email}`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open sender"
                >
                  <ExternalLink size={13} />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sb-modal__footer">
          <span className="sb-modal__footer-hint">
            Subscriptions are detected automatically from your inbox.
          </span>
          <button className="sb-btn sb-btn--primary" onClick={onClose}>Done</button>
        </div>
      </motion.div>
    </>,
    document.body,
  )
}

// ─── Manage Labels Modal ──────────────────────────────────────────────────────

function ManageLabelsModal({ onClose }: { onClose: () => void }) {
  const { labels } = useMailStore()
  const userLabels = labels.filter((l) => !l.is_system)

  // Local editable copy
  const [localLabels, setLocalLabels] = useState<Label[]>(userLabels)
  const [editId, setEditId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(LABEL_COLORS[0])
  const editInputRef = useRef<HTMLInputElement>(null)

  const startEdit = (label: Label) => {
    setEditId(label.id)
    setEditName(label.name)
    setEditColor(label.color)
    setTimeout(() => editInputRef.current?.focus(), 0)
  }

  const commitEdit = () => {
    if (!editId || !editName.trim()) { setEditId(null); return }
    setLocalLabels((prev) =>
      prev.map((l) => l.id === editId ? { ...l, name: editName.trim(), color: editColor } : l)
    )
    setEditId(null)
  }

  const deleteLabel = (id: string) =>
    setLocalLabels((prev) => prev.filter((l) => l.id !== id))

  const createLabel = () => {
    if (!newName.trim()) return
    const id = `label-${Date.now()}`
    setLocalLabels((prev) => [...prev, {
      id,
      name: newName.trim(),
      slug: newName.trim().toLowerCase().replace(/\s+/g, '-'),
      color: newColor,
      is_system: false,
      type: 'label',
      unread_count: 0,
      total_count: 0,
    }])
    setNewName('')
    setNewColor(LABEL_COLORS[Math.floor(Math.random() * LABEL_COLORS.length)])
  }

  return createPortal(
    <>
      <motion.div
        className="sb-modal-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="sb-modal sb-labels-modal"
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        role="dialog"
        aria-modal="true"
        aria-label="Manage labels"
      >
        {/* Header */}
        <div className="sb-modal__header">
          <div className="sb-modal__title-row">
            <Tag size={16} style={{ color: 'var(--brand)' }} />
            <h2>Manage Labels</h2>
            <span className="sb-badge">{localLabels.length}</span>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={15} /></button>
        </div>

        {/* Label list */}
        <div className="sb-labels__list">
          {localLabels.length === 0 && (
            <div className="sb-empty">No custom labels yet. Create one below.</div>
          )}
          {localLabels.map((label) => (
            <div key={label.id} className="sb-label-row">
              {editId === label.id ? (
                /* Edit mode */
                <div className="sb-label-row__edit">
                  <div className="sb-color-picker">
                    {LABEL_COLORS.map((c) => (
                      <button
                        key={c}
                        className={`sb-color-swatch${editColor === c ? ' sb-color-swatch--active' : ''}`}
                        style={{ background: c }}
                        onClick={() => setEditColor(c)}
                        aria-label={c}
                      />
                    ))}
                  </div>
                  <input
                    ref={editInputRef}
                    className="sb-label-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditId(null) }}
                    placeholder="Label name"
                  />
                  <button className="sb-action-btn sb-action-btn--confirm" onClick={commitEdit} title="Save">
                    <Check size={13} />
                  </button>
                  <button className="sb-action-btn" onClick={() => setEditId(null)} title="Cancel">
                    <X size={13} />
                  </button>
                </div>
              ) : (
                /* View mode */
                <>
                  <Circle size={10} style={{ color: label.color, fill: label.color, flexShrink: 0 }} />
                  <span className="sb-label-row__name">{label.name}</span>
                  {label.total_count > 0 && (
                    <span className="sb-label-row__count">{label.total_count}</span>
                  )}
                  <div className="sb-label-row__actions">
                    <button className="sb-action-btn" title="Edit" onClick={() => startEdit(label)}>
                      <Pencil size={13} />
                    </button>
                    <button
                      className="sb-action-btn sb-action-btn--danger"
                      title="Delete"
                      onClick={() => deleteLabel(label.id)}
                    >
                      <Trash size={13} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Create new label */}
        <div className="sb-labels__create">
          <div className="sb-labels__create-title">New label</div>
          <div className="sb-labels__create-row">
            <div className="sb-color-picker">
              {LABEL_COLORS.map((c) => (
                <button
                  key={c}
                  className={`sb-color-swatch${newColor === c ? ' sb-color-swatch--active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setNewColor(c)}
                  aria-label={c}
                />
              ))}
            </div>
            <input
              className="sb-label-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createLabel() }}
              placeholder="Label name…"
            />
            <button
              className="sb-btn sb-btn--primary"
              onClick={createLabel}
              disabled={!newName.trim()}
            >
              <Plus size={13} />
              Create
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="sb-modal__footer">
          <span className="sb-modal__footer-hint">
            Labels help organise your inbox. Drag to reorder.
          </span>
          <button className="sb-btn sb-btn--primary" onClick={onClose}>Done</button>
        </div>
      </motion.div>
    </>,
    document.body,
  )
}

// ─── Sidebar item ─────────────────────────────────────────────────────────────

interface SidebarItemProps {
  label: Label
  isActive: boolean
  collapsed: boolean
  onClick: () => void
}

function SidebarItem({ label, isActive, collapsed, onClick }: SidebarItemProps) {
  const Icon = SYSTEM_ICONS[label.slug]

  return (
    <button
      onClick={onClick}
      className={`sidebar-item${isActive ? ' sidebar-item--active' : ''}`}
      aria-current={isActive ? 'page' : undefined}
      title={collapsed ? label.name : undefined}
    >
      <span className="sidebar-item__icon">
        {Icon ? (
          <Icon size={16} />
        ) : (
          <span className="sidebar-item__dot" style={{ background: label.color }} />
        )}
      </span>

      {!collapsed && (
        <>
          <span className="sidebar-item__label">{label.name}</span>
          {label.unread_count > 0 && (
            <motion.span
              className="sidebar-item__badge"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              key={label.unread_count}
            >
              {label.unread_count > 99 ? '99+' : label.unread_count}
            </motion.span>
          )}
        </>
      )}

      {/* dot on icon when collapsed + unread */}
      {collapsed && label.unread_count > 0 && (
        <span className="sidebar-item__dot-badge" />
      )}
    </button>
  )
}

// ─── Section toggle button (More / Less / Categories) ────────────────────────

interface ToggleRowProps {
  label: string
  open: boolean
  collapsed: boolean
  icon?: React.ElementType
  onToggle: () => void
}

function ToggleRow({ label, open, collapsed, icon: Icon, onToggle }: ToggleRowProps) {
  if (collapsed) {
    return (
      <button className="sidebar-item sidebar-item--toggle" onClick={onToggle} title={label}>
        <span className="sidebar-item__icon">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </button>
    )
  }
  return (
    <button className="sidebar-item sidebar-item--toggle" onClick={onToggle}>
      <span className="sidebar-item__icon">
        {Icon ? <Icon size={16} /> : (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
      </span>
      <span className="sidebar-item__label">{label}</span>
      <span className="sidebar-item__toggle-arrow">
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </span>
    </button>
  )
}

// ─── Footer action ────────────────────────────────────────────────────────────

interface FooterActionProps {
  icon: React.ElementType
  label: string
  collapsed: boolean
  onClick?: () => void
}

function FooterAction({ icon: Icon, label, collapsed, onClick }: FooterActionProps) {
  return (
    <button className="sidebar-footer-action" onClick={onClick} title={collapsed ? label : undefined}>
      <span className="sidebar-item__icon"><Icon size={15} /></span>
      {!collapsed && <span className="sidebar-item__label">{label}</span>}
    </button>
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

interface SidebarProps {
  onCompose: () => void
  onOpenCustomizer?: () => void
}

export function Sidebar({ onCompose, onOpenCustomizer }: SidebarProps) {
  const { activeFolder, labels, setActiveFolder, isSyncing } = useMailStore()
  const { sidebarCollapsed, toggleSidebar } = useUIStore()

  // Expansion state
  const [moreOpen, setMoreOpen]             = useState(false)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [labelsOpen, setLabelsOpen]         = useState(true)

  // Modal state
  const [showSubscriptions, setShowSubscriptions] = useState(false)
  const [showLabels, setShowLabels]               = useState(false)

  const collapsed = sidebarCollapsed

  // Detect mobile viewport — sidebar becomes an overlay drawer on mobile
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const update = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
      // Auto-close sidebar when entering mobile, auto-open when entering desktop
      if (e.matches) {
        useUIStore.getState().setSidebarCollapsed(true)
      }
    }
    mq.addEventListener('change', update)
    // On first render in mobile: ensure sidebar starts hidden
    if (window.innerWidth <= 768) {
      useUIStore.getState().setSidebarCollapsed(true)
    }
    return () => mq.removeEventListener('change', update)
  }, [])

  // Split labels into groups
  const PRIMARY_SLUGS  = ['inbox', 'starred', 'snoozed', 'sent', 'drafts']
  const MORE_SLUGS     = ['important', 'scheduled', 'all_mail', 'spam', 'trash']
  const CATEGORY_SLUGS = ['social', 'updates', 'forums', 'promotions']

  const bySlug = (slugs: string[]) =>
    slugs.map((s) => labels.find((l) => l.slug === s)).filter(Boolean) as Label[]

  const primary    = bySlug(PRIMARY_SLUGS)
  const moreItems  = bySlug(MORE_SLUGS)
  const categories = bySlug(CATEGORY_SLUGS)
  const userLabels = labels.filter((l) => !l.is_system)

  const item = (label: Label) => (
    <SidebarItem
      key={label.id}
      label={label}
      isActive={activeFolder === label.slug}
      collapsed={collapsed}
      onClick={() => {
        setActiveFolder(label.slug)
        // Auto-close sidebar after navigation on mobile
        if (isMobile && !collapsed) toggleSidebar()
      }}
    />
  )

  return (
    <>
      {/* Mobile backdrop — tapping it closes the sidebar */}
      <AnimatePresence>
        {isMobile && !collapsed && (
          <motion.div
            className="sidebar-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={toggleSidebar}
          />
        )}
      </AnimatePresence>

      <aside
        className={[
          'sidebar',
          !isMobile && collapsed ? 'sidebar--collapsed' : '',
          isMobile             ? 'sidebar--mobile'    : '',
          isMobile && collapsed ? 'sidebar--hidden'    : '',
        ].filter(Boolean).join(' ')}
      >
        {/* ── Header ── */}
        <div className="sidebar__header">
          <AnimatePresence mode="wait">
            {!collapsed && (
              <motion.div
                className="sidebar__logo"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.15 }}
              >
                <span className="sidebar__logo-mark">T</span>
                <span className="sidebar__logo-text">Mail</span>
                {isSyncing && (
                  <RefreshCw size={12} className="animate-spin" style={{ color: 'var(--amber)', marginLeft: 4 }} />
                )}
              </motion.div>
            )}
          </AnimatePresence>
          {collapsed && (
            <button className="btn-icon sidebar__toggle" onClick={toggleSidebar} aria-label="Expand sidebar" title="Expand sidebar">
              <Menu size={16} />
            </button>
          )}
        </div>

        {/* ── Compose ── */}
        <div className="sidebar__compose">
          <motion.button
            className="sidebar__compose-btn"
            onClick={onCompose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            title="Compose (c)"
          >
            <Plus size={16} />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                >
                  Compose
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* ── Nav ── */}
        <nav className="sidebar__nav">

          {/* Primary folders */}
          {primary.map(item)}

          {/* Categories toggle + items */}
          <ToggleRow
            label="Categories"
            open={categoriesOpen}
            collapsed={collapsed}
            onToggle={() => setCategoriesOpen((v) => !v)}
          />
          <AnimatePresence initial={false}>
            {categoriesOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                style={{ overflow: 'hidden' }}
              >
                {categories.map(item)}
              </motion.div>
            )}
          </AnimatePresence>

          {/* More / Less toggle */}
          <ToggleRow
            label={moreOpen ? 'Less' : 'More'}
            open={moreOpen}
            collapsed={collapsed}
            onToggle={() => setMoreOpen((v) => !v)}
          />
          <AnimatePresence initial={false}>
            {moreOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                style={{ overflow: 'hidden' }}
              >
                {moreItems.map(item)}

                {/* Management actions (only shown expanded + not collapsed) */}
                {!collapsed && (
                  <div className="sidebar__manage-actions">
                    <FooterAction
                      icon={MailPlus}
                      label="Manage subscriptions"
                      collapsed={collapsed}
                      onClick={() => setShowSubscriptions(true)}
                    />
                    <FooterAction
                      icon={Settings2}
                      label="Manage labels"
                      collapsed={collapsed}
                      onClick={() => setShowLabels(true)}
                    />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Labels section ── */}
          {userLabels.length > 0 && (
            <>
              <div className="sidebar__section-header">
                <button
                  className="sidebar__section-toggle"
                  onClick={() => setLabelsOpen((v) => !v)}
                  title={collapsed ? 'Labels' : undefined}
                >
                  {!collapsed && <span>Labels</span>}
                  <span className="sidebar__section-header-actions">
                    {!collapsed && (
                      labelsOpen
                        ? <ChevronDown size={12} />
                        : <ChevronRight size={12} />
                    )}
                  </span>
                </button>
                {!collapsed && (
                  <button
                    className="btn-icon sidebar__section-add"
                    title="Create new label"
                    style={{ padding: '2px' }}
                    onClick={() => setShowLabels(true)}
                  >
                    <Plus size={13} />
                  </button>
                )}
              </div>

              <AnimatePresence initial={false}>
                {(labelsOpen || collapsed) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    style={{ overflow: 'hidden' }}
                  >
                    {userLabels.map(item)}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </nav>

        {/* ── Footer ── */}
        <div className="sidebar__footer">
          <button
            className="btn-icon"
            title="Theme Studio"
            onClick={onOpenCustomizer}
          >
            <Settings size={15} />
          </button>
        </div>

        <style>{`
          .sidebar {
            display: flex;
            flex-direction: column;
            width: var(--sidebar-width);
            min-width: var(--sidebar-width);
            height: 100%;
            background: var(--bg-sidebar);
            border-right: 1px solid var(--border);
            overflow-y: auto;
            overflow-x: hidden;
            flex-shrink: 0;
            position: relative;
            z-index: 10;
            /* Desktop collapse animation — pure CSS, no framer-motion */
            transition: width 0.2s cubic-bezier(0.16, 1, 0.3, 1),
                        min-width 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          }

          /* Desktop: icon-only collapsed state */
          .sidebar--collapsed {
            width: 56px;
            min-width: 56px;
          }

          /* Mobile drawer overlay */
          .sidebar-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.55);
            z-index: 199;
          }

          /* Mobile: fixed overlay drawer */
          .sidebar--mobile {
            position: fixed;
            top: 0;
            left: 0;
            height: 100dvh;
            width: var(--sidebar-width);
            min-width: var(--sidebar-width);
            z-index: 200;
            box-shadow: var(--shadow-xl);
            transform: translateX(0);
            transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
            /* No framer-motion interference — plain CSS controls transform */
          }

          /* Mobile: hidden (off-screen left) */
          .sidebar--hidden {
            transform: translateX(-100%);
          }

          .sidebar__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 12px 8px;
            min-height: 56px;
          }

          .sidebar__logo {
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .sidebar__logo-mark {
            width: 28px;
            height: 28px;
            background: var(--brand);
            color: white;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 14px;
            flex-shrink: 0;
          }

          .sidebar__logo-text {
            font-size: 17px;
            font-weight: 700;
            letter-spacing: -0.3px;
            color: var(--text-primary);
          }

          .sidebar__toggle {
            flex-shrink: 0;
            margin-left: auto;
          }

          .sidebar__compose {
            padding: 8px 12px 12px;
          }

          .sidebar__compose-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            padding: 9px 14px;
            background: var(--brand);
            color: white;
            border: none;
            border-radius: var(--radius-md);
            font-family: var(--font-ui);
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            overflow: hidden;
            white-space: nowrap;
            justify-content: center;
          }
          .sidebar__compose-btn:hover { background: var(--brand-hover); }

          .sidebar__nav {
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            padding: 4px 8px;
          }

          /* ─── Nav items ── */

          .sidebar-item {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 7px 12px 7px 10px;
            border: none;
            border-radius: var(--radius-md);
            background: transparent;
            color: var(--text-secondary);
            font-family: var(--font-ui);
            font-size: 13px;
            font-weight: 400;
            cursor: pointer;
            text-align: left;
            transition: background var(--duration-fast), color var(--duration-fast);
            white-space: nowrap;
            overflow: visible;
            position: relative;
          }

          .sidebar-item:hover {
            background: var(--bg-hover);
            color: var(--text-primary);
          }

          .sidebar-item--active {
            background: var(--bg-selected-strong);
            color: var(--text-primary);
            font-weight: 500;
          }

          .sidebar-item--toggle {
            color: var(--text-tertiary);
            font-size: 12px;
          }
          .sidebar-item--toggle:hover { color: var(--text-primary); }

          .sidebar-item__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: 18px;
            position: relative;
          }

          .sidebar-item__dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            flex-shrink: 0;
          }

          .sidebar-item__dot-badge {
            position: absolute;
            top: -2px;
            right: -3px;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: var(--amber);
            border: 1.5px solid var(--bg-sidebar);
          }

          .sidebar-item__label {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .sidebar-item__badge {
            display: inline-flex;
            align-items: center;
            min-width: 20px;
            padding: 1px 5px;
            background: var(--amber-subtle);
            color: var(--amber);
            border-radius: var(--radius-full);
            font-size: 10px;
            font-weight: 600;
            flex-shrink: 0;
          }

          .sidebar-item__toggle-arrow {
            color: var(--text-tertiary);
            flex-shrink: 0;
            display: flex;
            align-items: center;
          }

          /* ─── Section headers ── */

          .sidebar__section-header {
            display: flex;
            align-items: center;
            padding: 12px 4px 4px;
            gap: 4px;
          }

          .sidebar__section-toggle {
            display: flex;
            align-items: center;
            gap: 4px;
            flex: 1;
            background: none;
            border: none;
            cursor: pointer;
            color: var(--text-tertiary);
            font-size: 10px;
            font-weight: 600;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            padding: 2px 6px;
            border-radius: var(--radius-sm);
          }
          .sidebar__section-toggle:hover { color: var(--text-secondary); background: var(--bg-hover); }

          .sidebar__section-header-actions {
            display: flex;
            align-items: center;
            margin-left: auto;
          }

          .sidebar__section-add {
            flex-shrink: 0;
          }

          /* ─── Management actions inside More ── */

          .sidebar__manage-actions {
            border-top: 1px solid var(--border);
            margin-top: 4px;
            padding-top: 4px;
          }

          .sidebar-footer-action {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 7px 10px;
            border: none;
            border-radius: var(--radius-md);
            background: transparent;
            color: var(--text-tertiary);
            font-family: var(--font-ui);
            font-size: 12px;
            cursor: pointer;
            text-align: left;
            white-space: nowrap;
            overflow: hidden;
            transition: background var(--duration-fast), color var(--duration-fast);
          }
          .sidebar-footer-action:hover { background: var(--bg-hover); color: var(--text-secondary); }

          /* ─── Footer ── */

          .sidebar__footer {
            padding: 10px 12px;
            border-top: 1px solid var(--border);
            display: flex;
            align-items: center;
            gap: 8px;
          }

          /* ══════════════════════════════════════════
             Shared modal styles (sb-*)
          ══════════════════════════════════════════ */

          .sb-modal-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,.55);
            z-index: 1500;
          }

          .sb-modal {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 1600;
            background: var(--bg-surface);
            border: 1px solid var(--border-strong);
            border-radius: var(--radius-lg);
            box-shadow: var(--shadow-xl);
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .sb-modal__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 20px;
            border-bottom: 1px solid var(--border);
            flex-shrink: 0;
          }

          .sb-modal__title-row {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .sb-modal__title-row h2 {
            font-size: 15px;
            font-weight: 600;
            color: var(--text-primary);
          }

          .sb-badge {
            display: inline-flex;
            align-items: center;
            padding: 1px 7px;
            background: var(--bg-elevated);
            border: 1px solid var(--border);
            border-radius: var(--radius-full);
            font-size: 11px;
            color: var(--text-tertiary);
          }

          .sb-modal__footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 20px;
            border-top: 1px solid var(--border);
            flex-shrink: 0;
            gap: 12px;
          }

          .sb-modal__footer-hint {
            font-size: 11px;
            color: var(--text-tertiary);
          }

          .sb-btn {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 6px 14px;
            border-radius: var(--radius-md);
            font-family: var(--font-ui);
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            border: 1px solid var(--border);
            background: var(--bg-elevated);
            color: var(--text-primary);
            transition: background var(--duration-fast);
            flex-shrink: 0;
          }
          .sb-btn:hover { background: var(--bg-hover); }
          .sb-btn--primary {
            background: var(--brand);
            border-color: var(--brand);
            color: #fff;
          }
          .sb-btn--primary:hover { background: var(--brand-hover); border-color: var(--brand-hover); }
          .sb-btn:disabled { opacity: 0.45; cursor: not-allowed; }

          .sb-empty {
            padding: 32px 20px;
            text-align: center;
            color: var(--text-tertiary);
            font-size: 13px;
          }

          /* ── Action buttons ── */
          .sb-action-btn {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 4px 8px;
            border-radius: var(--radius-sm);
            font-family: var(--font-ui);
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            border: 1px solid var(--border);
            background: var(--bg-elevated);
            color: var(--text-secondary);
            white-space: nowrap;
            transition: background var(--duration-fast), color var(--duration-fast);
            flex-shrink: 0;
          }
          .sb-action-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
          .sb-action-btn--danger { color: var(--red); border-color: transparent; background: transparent; }
          .sb-action-btn--danger:hover { background: var(--red-subtle, rgba(239,68,68,.08)); color: var(--red); }
          .sb-action-btn--confirm { color: var(--green); border-color: transparent; background: transparent; }
          .sb-action-btn--confirm:hover { background: var(--green-subtle, rgba(109,184,122,.1)); }

          /* ══════════════════════════════════════════
             Subscriptions modal
          ══════════════════════════════════════════ */

          .sb-subs-modal {
            width: min(680px, 95vw);
            max-height: min(680px, 90vh);
          }

          .sb-subs__toolbar {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-bottom: 1px solid var(--border);
            flex-shrink: 0;
            flex-wrap: wrap;
          }

          .sb-search {
            flex: 1;
            min-width: 140px;
            padding: 6px 10px;
            border-radius: var(--radius-md);
            border: 1px solid var(--border);
            background: var(--bg-elevated);
            color: var(--text-primary);
            font-family: var(--font-ui);
            font-size: 13px;
            outline: none;
          }
          .sb-search:focus { border-color: var(--brand); }

          .sb-filter-tabs {
            display: flex;
            gap: 2px;
            background: var(--bg-elevated);
            border: 1px solid var(--border);
            border-radius: var(--radius-md);
            padding: 2px;
          }

          .sb-filter-tab {
            padding: 4px 10px;
            border-radius: calc(var(--radius-md) - 2px);
            border: none;
            background: transparent;
            color: var(--text-tertiary);
            font-family: var(--font-ui);
            font-size: 12px;
            cursor: pointer;
            transition: background var(--duration-fast), color var(--duration-fast);
            white-space: nowrap;
          }
          .sb-filter-tab:hover { color: var(--text-primary); }
          .sb-filter-tab--active { background: var(--bg-surface); color: var(--text-primary); font-weight: 500; }

          .sb-subs__list {
            flex: 1;
            overflow-y: auto;
            padding: 8px 0;
          }

          .sb-sub-row {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 20px;
            transition: background var(--duration-fast);
          }
          .sb-sub-row:hover { background: var(--bg-hover); }
          .sb-sub-row--muted { opacity: 0.6; }

          .sb-sub-row__avatar {
            width: 34px;
            height: 34px;
            border-radius: 10px;
            background: var(--brand-subtle, rgba(79,142,247,.12));
            color: var(--brand);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 700;
            flex-shrink: 0;
          }

          .sb-sub-row__info {
            flex: 1;
            min-width: 0;
          }

          .sb-sub-row__name {
            font-size: 13px;
            font-weight: 500;
            color: var(--text-primary);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .sb-sub-row__email {
            font-size: 11px;
            color: var(--text-tertiary);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .sb-sub-row__meta {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 2px;
            flex-shrink: 0;
          }

          .sb-sub-row__last {
            font-size: 11px;
            color: var(--text-tertiary);
            white-space: nowrap;
          }

          .sb-sub-row__count {
            font-size: 11px;
            color: var(--text-tertiary);
          }

          .sb-sub-row__status { flex-shrink: 0; }

          .sb-sub-row__actions {
            display: flex;
            align-items: center;
            gap: 4px;
            flex-shrink: 0;
          }

          .sb-pill {
            display: inline-flex;
            align-items: center;
            padding: 2px 8px;
            border-radius: var(--radius-full);
            font-size: 11px;
            font-weight: 500;
          }
          .sb-pill--green { background: rgba(109,184,122,.15); color: var(--green); }
          .sb-pill--gray  { background: var(--bg-elevated); color: var(--text-tertiary); }
          .sb-pill--red   { background: rgba(239,68,68,.1); color: var(--red); }

          /* ══════════════════════════════════════════
             Labels modal
          ══════════════════════════════════════════ */

          .sb-labels-modal {
            width: min(520px, 95vw);
            max-height: min(600px, 90vh);
          }

          .sb-labels__list {
            flex: 1;
            overflow-y: auto;
            padding: 8px 0;
          }

          .sb-label-row {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 20px;
            transition: background var(--duration-fast);
            min-height: 40px;
          }
          .sb-label-row:hover { background: var(--bg-hover); }
          .sb-label-row:hover .sb-label-row__actions { opacity: 1; }

          .sb-label-row__name {
            flex: 1;
            font-size: 13px;
            color: var(--text-primary);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .sb-label-row__count {
            font-size: 11px;
            color: var(--text-tertiary);
            flex-shrink: 0;
          }

          .sb-label-row__actions {
            display: flex;
            align-items: center;
            gap: 4px;
            opacity: 0;
            transition: opacity var(--duration-fast);
            flex-shrink: 0;
          }

          .sb-label-row__edit {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
            flex-wrap: wrap;
          }

          .sb-labels__create {
            padding: 12px 20px 4px;
            border-top: 1px solid var(--border);
            flex-shrink: 0;
          }

          .sb-labels__create-title {
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: var(--text-tertiary);
            margin-bottom: 8px;
          }

          .sb-labels__create-row {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }

          .sb-color-picker {
            display: flex;
            gap: 4px;
            flex-wrap: wrap;
            flex-shrink: 0;
          }

          .sb-color-swatch {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 2px solid transparent;
            cursor: pointer;
            transition: transform var(--duration-fast);
            padding: 0;
          }
          .sb-color-swatch:hover { transform: scale(1.2); }
          .sb-color-swatch--active {
            border-color: var(--text-primary);
            transform: scale(1.15);
          }

          .sb-label-input {
            flex: 1;
            min-width: 120px;
            padding: 6px 10px;
            border-radius: var(--radius-md);
            border: 1px solid var(--border);
            background: var(--bg-elevated);
            color: var(--text-primary);
            font-family: var(--font-ui);
            font-size: 13px;
            outline: none;
          }
          .sb-label-input:focus { border-color: var(--brand); }
        `}</style>
      </aside>

      {/* ── Modals (rendered via portal outside the aside) ── */}
      <AnimatePresence>
        {showSubscriptions && (
          <ManageSubscriptionsModal onClose={() => setShowSubscriptions(false)} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showLabels && (
          <ManageLabelsModal onClose={() => setShowLabels(false)} />
        )}
      </AnimatePresence>
    </>
  )
}
